'use client'

import * as React from 'react'
import { cn } from '../../lib/utils'
import { Button } from './button'
import { Input } from './input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './select'
import { useIsRTLDirection } from '../../hooks'
import { Search, Filter, List, LayoutGrid, ArrowUpDown, X } from 'lucide-react'

// ============================================
// Types
// ============================================

export interface FilterOption {
  value: string
  label: string
  icon?: React.ReactNode
}

export interface FilterGroup {
  /** Unique identifier */
  id: string
  /** Display label */
  label: string
  /** Available options */
  options: FilterOption[]
  /** Currently selected value(s) */
  value?: string | string[]
  /** Allow multiple selections (coming soon) */
  multiple?: boolean
  /** Placeholder text */
  placeholder?: string
  /** Width class (default: w-40) */
  width?: string
}

export interface SortOption {
  /** Combined value (e.g., 'createdAt:desc') */
  value: string
  /** Display label (e.g., 'Newest first') */
  label: string
}

export interface PageToolbarProps {
  /** Search placeholder */
  searchPlaceholder?: string
  /** Search value */
  searchValue?: string
  /** Search change handler */
  onSearchChange?: (value: string) => void
  /** Filter options (legacy single filter) */
  filterOptions?: FilterOption[]
  /** Current filter value (legacy single filter) */
  filterValue?: string
  /** Filter change handler (legacy single filter) */
  onFilterChange?: (value: string) => void
  /** Filter label (legacy single filter) */
  filterLabel?: string
  /** Multiple filter groups */
  filterGroups?: FilterGroup[]
  /** Filter group change handler */
  onFilterGroupChange?: (groupId: string, value: string | string[]) => void
  /** Sort options */
  sortOptions?: SortOption[]
  /** Current sort value */
  sortValue?: string
  /** Sort change handler */
  onSortChange?: (value: string) => void
  /** Sort label */
  sortLabel?: string
  /** Show active filters as chips */
  showActiveFilters?: boolean
  /** Clear single filter */
  onClearFilter?: (groupId: string) => void
  /** Clear all filters */
  onClearAllFilters?: () => void
  /** Show view toggle */
  showViewToggle?: boolean
  /** Current view mode */
  viewMode?: 'table' | 'cards'
  /** View mode change handler */
  onViewModeChange?: (mode: 'table' | 'cards') => void
  /** Additional toolbar content */
  children?: React.ReactNode
  /** Additional className */
  className?: string
  /**
   * @deprecated No longer needed - RTL is detected automatically from document direction
   */
  isRTL?: boolean
}

// ============================================
// Helper Functions
// ============================================

function getActiveFilters(filterGroups: FilterGroup[]): Array<{
  groupId: string
  groupLabel: string
  value: string
  label: string
}> {
  const active: Array<{
    groupId: string
    groupLabel: string
    value: string
    label: string
  }> = []

  for (const group of filterGroups) {
    const values = Array.isArray(group.value) ? group.value : [group.value]

    for (const value of values) {
      if (!value || value === 'all') continue

      const option = group.options.find((o) => o.value === value)
      if (option) {
        active.push({
          groupId: group.id,
          groupLabel: group.label,
          value,
          label: option.label,
        })
      }
    }
  }

  return active
}

// ============================================
// Component
// ============================================

/**
 * Unified page toolbar component
 * Displays search, filters, sorting, and view toggle consistently across all pages
 *
 * RTL Support: Uses JavaScript-based detection from document.documentElement.dir
 * This is the most reliable approach as it doesn't depend on CSS variants.
 *
 * @example
 * ```tsx
 * <PageToolbar
 *   searchValue={filter.search}
 *   onSearchChange={(search) => dispatch(actions.setFilter({ search }))}
 *   filterGroups={[
 *     {
 *       id: 'status',
 *       label: 'Status',
 *       options: statusOptions,
 *       value: filter.status,
 *     },
 *     {
 *       id: 'type',
 *       label: 'Type',
 *       options: typeOptions,
 *       value: filter.type,
 *     },
 *   ]}
 *   onFilterGroupChange={(groupId, value) =>
 *     dispatch(actions.setFilter({ [groupId]: value }))
 *   }
 *   sortOptions={sortOptions}
 *   sortValue={`${filter.sortBy}:${filter.sortOrder}`}
 *   onSortChange={(value) => {
 *     const [sortBy, sortOrder] = value.split(':')
 *     dispatch(actions.setFilter({ sortBy, sortOrder }))
 *   }}
 *   showActiveFilters
 *   onClearFilter={(groupId) => dispatch(actions.setFilter({ [groupId]: 'all' }))}
 *   onClearAllFilters={() => dispatch(actions.resetFilters())}
 * />
 * ```
 */
export function PageToolbar({
  searchPlaceholder = 'Search...',
  searchValue = '',
  onSearchChange,
  filterOptions,
  filterValue = 'all',
  onFilterChange,
  filterLabel,
  filterGroups,
  onFilterGroupChange,
  sortOptions,
  sortValue,
  onSortChange,
  sortLabel = 'Sort',
  showActiveFilters = false,
  onClearFilter,
  onClearAllFilters,
  showViewToggle = false,
  viewMode = 'table',
  onViewModeChange,
  children,
  className,
  isRTL: isRTLProp,
}: PageToolbarProps) {
  // Use hook for automatic RTL detection, with prop as override
  const isRTLFromHook = useIsRTLDirection()
  const isRTL = isRTLProp ?? isRTLFromHook

  // Calculate active filters for chips
  const activeFilters = filterGroups ? getActiveFilters(filterGroups) : []
  const hasActiveFilters = activeFilters.length > 0

  return (
    <div className={cn('space-y-3', className)}>
      {/* Main toolbar row - no flex-row-reverse, RTL handled by document direction */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search - uses logical properties (start-3, ps-10) that flip in RTL */}
        {onSearchChange && (
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground start-3"
            />
            <Input
              placeholder={searchPlaceholder}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="h-10 glass-card border-0 rounded-xl focus:ring-2 focus:ring-primary/20 ps-10"
            />
          </div>
        )}

        {/* Legacy single filter (backwards compatible) */}
        {filterOptions && filterOptions.length > 0 && onFilterChange && (
          <Select value={filterValue} onValueChange={onFilterChange}>
            <SelectTrigger className="w-40 h-10 glass-card border-0">
              <Filter className="w-4 h-4 me-2" />
              <SelectValue placeholder={filterLabel} />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Multiple filter groups */}
        {filterGroups &&
          filterGroups.length > 0 &&
          onFilterGroupChange &&
          filterGroups.map((group) => {
            const currentValue = Array.isArray(group.value)
              ? group.value[0] || 'all'
              : group.value || 'all'

            return (
              <Select
                key={group.id}
                value={currentValue}
                onValueChange={(value) => onFilterGroupChange(group.id, value)}
              >
                <SelectTrigger
                  className={cn(
                    'h-10 glass-card border-0',
                    group.width || 'w-40'
                  )}
                >
                  <Filter className="w-4 h-4 me-2" />
                  <SelectValue placeholder={group.placeholder || group.label} />
                </SelectTrigger>
                <SelectContent>
                  {group.options.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <span className="flex items-center gap-2">
                        {option.icon}
                        {option.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )
          })}

        {/* Sort selector */}
        {sortOptions && sortOptions.length > 0 && onSortChange && (
          <Select value={sortValue || ''} onValueChange={onSortChange}>
            <SelectTrigger className="w-44 h-10 glass-card border-0">
              <ArrowUpDown className="w-4 h-4 me-2" />
              <SelectValue placeholder={sortLabel} />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Additional content */}
        {children}

        {/* View Toggle */}
        {showViewToggle && onViewModeChange && (
          <div className="flex items-center glass-card rounded-xl p-1 ms-auto">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onViewModeChange('table')}
              className={cn(
                'h-8 w-8 rounded-lg',
                viewMode === 'table'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <List className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onViewModeChange('cards')}
              className={cn(
                'h-8 w-8 rounded-lg',
                viewMode === 'cards'
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Active filter chips - no flex-row-reverse, handled by document direction */}
      {showActiveFilters && hasActiveFilters && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-muted-foreground">Filters:</span>
          {activeFilters.map((filter) => (
            <Button
              key={`${filter.groupId}-${filter.value}`}
              variant="secondary"
              size="sm"
              className="h-7 px-2 rounded-lg text-xs gap-1"
              onClick={() => onClearFilter?.(filter.groupId)}
            >
              <span className="text-muted-foreground">{filter.groupLabel}:</span>
              <span>{filter.label}</span>
              <X className="w-3 h-3 ms-1 text-muted-foreground hover:text-foreground" />
            </Button>
          ))}
          {activeFilters.length > 1 && onClearAllFilters && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 rounded-lg text-xs text-muted-foreground hover:text-foreground"
              onClick={onClearAllFilters}
            >
              Clear all
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
